import { motion } from "framer-motion"
import { useInView } from "framer-motion"
import { useRef, useContext } from "react"
import { IntroContext } from "./IntroContext"
import './Main.css';
import './skill.css';
import './mediaMobile.css';
import { arrow, ps, pr, ai, ae, au, figma } from '../Images';
import Approach from "./approach";
import Works from "./works"
import Certis from "./certis"
import Footer from "./footer"
import Exp from "./exp"
import { Banner } from "./Banner";

const SKILL_GROUPS = [
    { title: "Languages", items: ["Python", "C++", "C", "Java", "JavaScript", "SQL"] },
    { title: "GenAI & LLMs", items: ["LangChain", "Chainlit", "OpenAI", "Gemini", "RAG", "Pinecone", "Vapi"] },
    { title: "Computer Vision", items: ["OpenCV", "YOLO", "Tesseract", "EasyOCR"] },
    { title: "ML & Data", items: ["TensorFlow", "NumPy", "Pandas"] },
    { title: "Deploy & Infra", items: ["Docker", "Kubernetes", "Flask", "Git"] },
];

export default function Main() {
    const images = [figma, ps, pr, ai, ae, au].map((Image) => ({
        id: crypto.randomUUID(),
        image: Image
    }));
    const nextSectionRef = useRef(null);
    const isInView = useInView(nextSectionRef, { once: true });
    const introDone = useContext(IntroContext);

    const heroContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.12, delayChildren: 0.15 },
        },
    };
    const heroItem = {
        hidden: { opacity: 0, y: 26 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] },
        },
    };

    const skillsContainerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } },
    };
    const skillsItemVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
    };

    return (
        <div>
            {/* HERO */}
            <section className="hero2">
                <div className={`hero2-rays ${introDone ? "lit" : ""}`} aria-hidden="true" />

                <motion.div
                    className="hero2-inner"
                    variants={heroContainer}
                    initial="hidden"
                    animate={introDone ? "visible" : "hidden"}
                >
                    <motion.div className="hero2-badge" variants={heroItem}>
                        <span className="hero2-badge-dot" />
                        Available for AI roles
                    </motion.div>

                    <motion.h1 className="hero2-headline" variants={heroItem}>
                        I am Swalih — an{" "}
                        <span className="accent">AI Engineer</span>{" "}
                        who builds intelligent experiences that connect and convert.
                    </motion.h1>

                    {/* SEO full name */}
                    <h2 className="hiddenForSEOPurposes">Mohammed Swalih Palan Padiyan</h2>

                    <motion.p className="hero2-role" variants={heroItem}>
                        Currently a B.Tech student at{" "}
                        <span className="accent">IIIT Kottayam</span>, previously an AI intern at{" "}
                        <span className="accent">Etisalat</span>.
                    </motion.p>

                    <motion.p className="hero2-sub" variants={heroItem}>
                        Also a UI/UX designer &amp; web developer.
                    </motion.p>

                    <motion.a
                        className="hero2-resume"
                        href="https://drive.google.com/file/d/1wVxU_Ksf-igyjAnQZBWk-4ksggfk8UUj/view?usp=drive_link"
                        target="_blank"
                        rel="noreferrer"
                        variants={heroItem}
                    >
                        Download Resume <span className="arw">↗</span>
                    </motion.a>
                </motion.div>

                <motion.img
                    className="hero2-chevron"
                    src={arrow}
                    alt="Scroll down"
                    onClick={() => document.getElementById('next-section')?.scrollIntoView({ behavior: 'smooth' })}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 1.2, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
                />
            </section>

            {/* SKILLS STRIP */}
            <motion.div
                className="next-section"
                id="next-section"
                ref={nextSectionRef}
                variants={skillsContainerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
            >
                <div className="section skillSection">
                    <motion.h3 className="sec-label" variants={skillsItemVariants}>Skills</motion.h3>
                    <motion.p className="sec-statement" variants={skillsItemVariants}>
                        Building with AI — <span className="mute">from models and pipelines</span> to the interface around them.
                    </motion.p>

                    <motion.div className="skillGroups" variants={skillsItemVariants}>
                        {SKILL_GROUPS.map((g) => (
                            <div className="skillGroup" key={g.title}>
                                <h4 className="skillGroupTitle">{g.title}</h4>
                                <div className="skillChips">
                                    {g.items.map((it) => <span className="skillChip" key={it}>{it}</span>)}
                                </div>
                            </div>
                        ))}
                    </motion.div>

                    <motion.h4 className="skillDesignLabel" variants={skillsItemVariants}>Design &amp; Creative</motion.h4>
                </div>
                <div className="scrollWrap">
                    <Banner images={images} speed={5000} />
                </div>
            </motion.div>

            <Approach />
            <Works />
            <Exp />
            <Certis />
            <Footer />
        </div>
    )
}
